'use client';

import { useTranslation } from '@crehana/i18n';

import { useLanguage } from '@/shared/components/language-provider';
import { useTalentAuth } from '@/shared/contexts/talent-auth/talent-auth.client';

import {
  type TalentSharedLabelsQueryQueryVariables,
  useTalentSharedLabelsQueryQuery,
} from './graphql/talent-get-labels-query.v5.generated';

export const useSetupTranslations = ({
  namespaces,
}: {
  namespaces: TalentSharedLabelsQueryQueryVariables['namespaces'];
}) => {
  const { user } = useTalentAuth();
  const language = useLanguage();
  const { addResourceBundle } = useTranslation();
  const {
    data: _data,
    loading,
    error,
  } = useTalentSharedLabelsQueryQuery({
    variables: {
      company_id: Number(user.company?.id),
      language,
      namespaces,
    },
    context: {
      clientName: 'v5',
    },
  });

  if (
    _data?.talent.get_talent_module_labels?.__typename ===
      'TalentResponseInternalError' ||
    _data?.talent.get_talent_module_labels?.__typename ===
      'TalentResponseValidationError' ||
    error
  ) {
    const errorMsg = `There was an error when trying to get the translation keys for the modules: ${namespaces}`;
    console.error(errorMsg);
    return {
      loading,
      error: errorMsg,
    };
  }

  const data = _data;
  const bundleTranslations = {
    [language]: {},
  };
  if (namespaces) {
    const values =
      typeof namespaces === 'string' ? namespaces.split(', ') : namespaces;
    values.forEach(namespace => {
      // @ts-expect-error temporal logic, need to confirm with backend what is the real shape of the data
      bundleTranslations[language][namespace] = {};
    });
  }
  if (
    data?.talent?.get_talent_module_labels?.__typename === 'LabelTalentResponse'
  ) {
    data.talent.get_talent_module_labels.labels?.forEach(label => {
      if (label) {
        // @ts-expect-error  this key/object is created in a previous step
        bundleTranslations[language][label.module][label.code] =
          label.description;
      }
    });
  }

  addResourceBundle(bundleTranslations);

  return {
    loading,
    error,
  };
};
